/**
 * Utilitários de Validação
 * 
 * Funções reutilizáveis para validação de dados
 */

/**
 * Valida formato de email
 * 
 * @param {string} email - Email a ser validado
 * @returns {boolean} True se válido
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Valida formato de CEP brasileiro
 * 
 * @param {string} cep - CEP a ser validado
 * @returns {boolean} True se válido
 */
export const isValidCEP = (cep) => {
  if (!cep || typeof cep !== 'string') return false;
  
  // Remove formatação
  const numbers = cep.replace(/\D/g, '');
  
  // CEP deve ter 8 dígitos
  return numbers.length === 8;
};

/**
 * Valida formato de CPF
 * 
 * @param {string} cpf - CPF a ser validado
 * @returns {boolean} True se válido
 */
export const isValidCPF = (cpf) => {
  if (!cpf || typeof cpf !== 'string') return false;
  
  // Remove formatação
  const numbers = cpf.replace(/\D/g, '');
  
  // CPF deve ter 11 dígitos
  if (numbers.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais (CPF inválido)
  if (/^(\d)\1{10}$/.test(numbers)) return false;
  
  // Validação dos dígitos verificadores
  let sum = 0;
  let remainder;
  
  // Valida primeiro dígito
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(numbers.substring(i - 1, i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(numbers.substring(9, 10))) return false;
  
  // Valida segundo dígito
  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(numbers.substring(i - 1, i)) * (12 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(numbers.substring(10, 11))) return false;
  
  return true;
};

/**
 * Valida formato de telefone brasileiro
 * 
 * @param {string} phone - Telefone a ser validado
 * @returns {boolean} True se válido
 */
export const isValidPhone = (phone) => {
  if (!phone || typeof phone !== 'string') return false;
  
  // Remove formatação
  const numbers = phone.replace(/\D/g, '');
  
  // Telefone deve ter 10 ou 11 dígitos (fixo ou celular)
  return numbers.length === 10 || numbers.length === 11;
};

/**
 * Valida se um valor não está vazio
 * 
 * @param {*} value - Valor a ser validado
 * @returns {boolean} True se não está vazio
 */
export const isNotEmpty = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

/**
 * Valida se um valor é um número válido
 * 
 * @param {*} value - Valor a ser validado
 * @param {Object} options - Opções de validação
 * @param {number} options.min - Valor mínimo
 * @param {number} options.max - Valor máximo
 * @returns {boolean} True se válido
 */
export const isValidNumber = (value, options = {}) => {
  const { min, max } = options;
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) return false;
  
  if (min !== undefined && num < min) return false;
  if (max !== undefined && num > max) return false;
  
  return true;
};

/**
 * Valida se uma string tem tamanho mínimo
 * 
 * @param {string} value - String a ser validada
 * @param {number} minLength - Tamanho mínimo
 * @returns {boolean} True se válido
 */
export const hasMinLength = (value, minLength) => {
  if (!value || typeof value !== 'string') return false;
  return value.trim().length >= minLength;
};

/**
 * Valida se uma string tem tamanho máximo
 * 
 * @param {string} value - String a ser validada
 * @param {number} maxLength - Tamanho máximo
 * @returns {boolean} True se válido
 */
export const hasMaxLength = (value, maxLength) => {
  if (!value || typeof value !== 'string') return true;
  return value.trim().length <= maxLength;
};

/**
 * Valida endereço completo
 * 
 * @param {Object} address - Objeto com dados do endereço
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validateAddress = (address) => {
  const errors = [];
  
  if (!address) {
    return { isValid: false, errors: ['Endereço não informado'] };
  }
  
  if (!isNotEmpty(address.address)) {
    errors.push('Rua/Avenida é obrigatória');
  }
  
  if (!isNotEmpty(address.number)) {
    errors.push('Número é obrigatório');
  }
  
  if (!isNotEmpty(address.neighborhood)) {
    errors.push('Bairro é obrigatório');
  }
  
  if (!isNotEmpty(address.city)) {
    errors.push('Cidade é obrigatória');
  }
  
  if (!isNotEmpty(address.state)) {
    errors.push('Estado é obrigatório');
  } else if (address.state.length !== 2) {
    errors.push('Estado deve ter 2 caracteres (ex: RJ, SP)');
  }
  
  if (!isValidCEP(address.postalCode)) {
    errors.push('CEP inválido');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Valida dados de checkout
 * 
 * @param {Object} checkoutData - Dados do checkout
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validateCheckout = (checkoutData) => {
  const errors = [];
  
  if (!checkoutData) {
    return { isValid: false, errors: ['Dados do checkout não informados'] };
  }
  
  // Valida email
  if (!isNotEmpty(checkoutData.email)) {
    errors.push('Email é obrigatório');
  } else if (!isValidEmail(checkoutData.email)) {
    errors.push('Email inválido');
  }
  
  // Valida nome (se não for retirada)
  if (!checkoutData.isPickup && !isNotEmpty(checkoutData.name)) {
    errors.push('Nome do destinatário é obrigatório');
  }
  
  // Valida endereço (se não for retirada)
  if (!checkoutData.isPickup) {
    const addressValidation = validateAddress(checkoutData.shippingAddress);
    if (!addressValidation.isValid) {
      errors.push(...addressValidation.errors);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Valida dados de produto
 * 
 * @param {Object} product - Dados do produto
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validateProduct = (product) => {
  const errors = [];
  
  if (!product) {
    return { isValid: false, errors: ['Dados do produto não informados'] };
  }
  
  if (!isNotEmpty(product.name)) {
    errors.push('Nome do produto é obrigatório');
  } else if (!hasMinLength(product.name, 3)) {
    errors.push('Nome do produto deve ter pelo menos 3 caracteres');
  }
  
  if (!isValidNumber(product.price, { min: 0.01 })) {
    errors.push('Preço inválido (deve ser maior que zero)');
  }
  
  if (!isValidNumber(product.stock, { min: 0 })) {
    errors.push('Estoque inválido (deve ser um número positivo)');
  }
  
  if (!isNotEmpty(product.category)) {
    errors.push('Categoria é obrigatória');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Valida dados de contato
 * 
 * @param {Object} contactData - Dados do formulário de contato
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validateContact = (contactData) => {
  const errors = [];
  
  if (!contactData) {
    return { isValid: false, errors: ['Dados de contato não informados'] };
  }
  
  if (!isNotEmpty(contactData.name)) {
    errors.push('Nome é obrigatório');
  } else if (!hasMinLength(contactData.name, 2)) {
    errors.push('Nome deve ter pelo menos 2 caracteres');
  }
  
  if (!isNotEmpty(contactData.email)) {
    errors.push('Email é obrigatório');
  } else if (!isValidEmail(contactData.email)) {
    errors.push('Email inválido');
  }
  
  if (!isNotEmpty(contactData.message)) {
    errors.push('Mensagem é obrigatória');
  } else if (!hasMinLength(contactData.message, 10)) {
    errors.push('Mensagem deve ter pelo menos 10 caracteres');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

